import type { TFunction } from 'i18next';
import { useMemo, useRef, useState } from 'react';
import validate from 'validate.js';

import type { ICampaignAttributes, ReportModel } from '@feathr/blackbox';
import { CampaignClass } from '@feathr/blackbox';
import { useAccount, useStore } from '@feathr/extender/state';
import { flattenError } from '@feathr/hooks';

export interface ICampaignFormReturn {
  campaign: ReportModel<ICampaignAttributes>;
  logoUrl: string;
  setLogoUrl: (url: string) => void;
  validationErrors: string[];
}

/**
 * Custom hook that manages campaign form state using MobX models
 */
export function useCampaignForm(t: TFunction): ICampaignFormReturn {
  const { Campaigns } = useStore();
  const account = useAccount();

  const campaignRef = useRef<ReportModel<ICampaignAttributes> | undefined>(undefined);
  // Initializing this way to avoid re-creating the ref contents
  if (campaignRef.current === undefined) {
    campaignRef.current = Campaigns.create({
      // Fixed as Retargeting
      _cls: CampaignClass.Segment,
      event: '',
    });
  }
  const campaign = campaignRef.current;

  const [logoUrl, setLogoUrlState] = useState(() => account.get('logo') || '');

  function setLogoUrl(url: string): void {
    setLogoUrlState(url);
  }

  const validationErrors = useMemo(() => {
    function getValidationErrors(): string[] {
      const errors = campaign.validate(['event', 'name'], false, 'flat').errors;

      const logoError: string[] =
        validate.single(logoUrl, {
          presence: {
            allowEmpty: false,
            message: t('^Organization logo is required.'),
          },
        }) || [];

      // Use flattenError instead of manual filtering
      return [...errors, ...flattenError(logoError)];
    }

    return getValidationErrors();
  }, [campaign, logoUrl, t, campaign.get('name'), campaign.get('event')]);

  return {
    campaign,
    logoUrl,
    setLogoUrl,
    validationErrors,
  };
}
